import React from "react";
import { Spin, Tabs } from "antd";
import WelcomeBrandKpiHeroCard from "./WelcomeBrandKpiHeroCard";
import WelcomeTabReviewCountButton from "./WelcomeTabReviewCountButton";
import {
  resolveBrandKpiHeroHeader,
  welcomeWidgetUsesBrandKpiHero,
} from "./welcomeBrandKpiHero";
import { welcomeWidgetUsesLazyTopicTabs } from "queries/welcomeWidgetData";
import styles from "pages/Welcome/style.module.scss";

/**
 * Per-topic tabs for brand KPI hero widgets (metrics 9, 10 & 29) when API returns topic_tabs.
 */
export default function WelcomeBrandKpiHeroTopicTabs({
  widget,
  data,
  since,
  until,
  t,
  i18n,
  lazyTopicTabKey,
  onLazyTopicTabKeyChange,
  onTopicTabLoad,
  isTopicTabPartialLoading,
  isPageDataLoading,
  parseTopicLibId,
  onTabReviewCountClick,
}) {
  const topicTabs = Array.isArray(data?.topic_tabs) ? data.topic_tabs : [];
  if (
    !welcomeWidgetUsesBrandKpiHero(widget) ||
    topicTabs.length === 0
  ) {
    return null;
  }

  const mid = Number(widget.metric_id);
  const lazyTopicTabs = welcomeWidgetUsesLazyTopicTabs(widget);

  const handleTopicTabSelect = (key) => {
    if (lazyTopicTabs && onLazyTopicTabKeyChange) {
      onLazyTopicTabKeyChange(key);
    }
    if (!lazyTopicTabs || !onTopicTabLoad || !parseTopicLibId) {
      return;
    }
    const lib = parseTopicLibId(widget, key);
    if (lib == null) {
      return;
    }
    const row = topicTabs.find((tab) => String(tab.id) === String(key));
    if (row && !row.payload_pending && row.payload != null) {
      return;
    }
    void onTopicTabLoad(widget, lib);
  };

  const activeKey = String(
    lazyTopicTabs && lazyTopicTabKey != null
      ? lazyTopicTabKey
      : topicTabs[0]?.id ?? "general",
  );

  const items = topicTabs.map((tab) => {
    const tp = tab.payload || {};
    const topicLibId =
      tab.id !== "general" && parseTopicLibId
        ? parseTopicLibId(widget, tab.id)
        : null;
    const tabPending =
      lazyTopicTabs &&
      tab.id !== "general" &&
      (tab.payload_pending ||
        tab.payload == null ||
        (topicLibId != null &&
          isTopicTabPartialLoading?.(widget.id, topicLibId)));
    const tabRcRaw = tab?.review_count;
    const brandKpiHeroHeader = resolveBrandKpiHeroHeader(tp, mid);
    const tabHeader =
      brandKpiHeroHeader != null
        ? {
            ...brandKpiHeroHeader,
            review_count:
              tabRcRaw != null &&
              tabRcRaw !== "" &&
              Number.isFinite(Number(tabRcRaw))
                ? Number(tabRcRaw)
                : brandKpiHeroHeader?.review_count,
          }
        : null;
    const topicReviewCountLabel =
      tabRcRaw != null &&
      tabRcRaw !== "" &&
      Number.isFinite(Number(tabRcRaw))
        ? new Intl.NumberFormat(
            i18n.language?.toLowerCase().startsWith("tr") ? "tr-TR" : "en-US",
          ).format(Number(tabRcRaw))
        : null;

    return {
      key: String(tab.id),
      label:
        tab.id === "general"
          ? t("welcome.genaiMultiTopicGeneralTab")
          : tab.label || String(tab.id),
      children: tabPending ? (
        <div
          className={`${styles.chartContent} ${styles.tabLoadingCenterTall}`}
        >
          <Spin size="large" />
        </div>
      ) : tabHeader ? (
        <>
          {topicReviewCountLabel != null && (
            <div className={styles.welcomeGeneralSummaryHierarchicalTabMeta}>
              <WelcomeTabReviewCountButton
                label={t("welcome.hierarchicalTabReviewCount", {
                  count: topicReviewCountLabel,
                })}
                reviewCount={tabRcRaw}
                onClick={
                  typeof onTabReviewCountClick === "function"
                    ? () =>
                        onTabReviewCountClick({
                          tab,
                          reviewCount: Number(tabRcRaw),
                          topicLibId:
                            tab.id !== "general" && parseTopicLibId
                              ? parseTopicLibId(widget, tab.id)
                              : null,
                          topicLabel:
                            tab.id === "general"
                              ? t("welcome.genaiMultiTopicGeneralTab")
                              : tab.label || String(tab.id),
                        })
                    : undefined
                }
              />
            </div>
          )}
          <WelcomeBrandKpiHeroCard
            metricId={mid}
            header={tabHeader}
            widget={widget}
            since={since}
            until={until}
            t={t}
            i18n={i18n}
            isPageDataLoading={isPageDataLoading}
            subtitleOverride={
              tab.id === "general"
                ? undefined
                : tab.label || String(tab.id)
            }
          />
        </>
      ) : (
        <div className={styles.tabLoadingCenter}>
          <Spin size="large" />
        </div>
      ),
    };
  });

  return (
    <div
      className={`${styles.chartContainer} ${styles.brandKpiHeroContainer} ${styles.chartContainerVariableHeight}`.trim()}
    >
      <Tabs
        className={styles.welcomeHierarchicalTabs}
        {...(lazyTopicTabs ? { activeKey } : {})}
        onChange={handleTopicTabSelect}
        items={items}
      />
    </div>
  );
}
